import * as React from 'react';
import { ArrowUpDown, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { FilterTabs } from '@/components/ui/filter-tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { IPostToolbarProps, IPostSortOption } from '@/@types/posts';

const SORT_OPTIONS: { value: IPostSortOption; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'title', label: 'Title' },
];

function PostToolbar({
  tabs,
  activeTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  sortOption,
  onSortChange,
}: IPostToolbarProps) {
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === sortOption)?.label ?? 'Newest';

  return (
    <div className="flex items-center justify-between">
      {/* Left Section */}
      <div className="flex items-center gap-3">
        <FilterTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={id => onTabChange(id as typeof activeTab)}
        />
        <SearchInput
          value={searchQuery}
          onChange={onSearchChange}
          placeholder="Search posts..."
          className="w-[320px]"
        />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {/* Sort Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 rounded-md border border-border bg-background-card px-3 py-2 text-[13px] font-medium text-foreground-muted hover:bg-border">
              <ArrowUpDown className="h-3.5 w-3.5" />
              <span>{currentSortLabel}</span>
              <ChevronDown className="h-3.5 w-3.5 text-foreground-placeholder" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SORT_OPTIONS.map(option => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onSortChange(option.value)}
                className={sortOption === option.value ? 'bg-muted' : ''}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Filter Button (placeholder for future filters) */}
        <button className="flex items-center gap-1.5 rounded-md border border-border bg-background-card px-3 py-2 text-[13px] font-medium text-foreground-muted hover:bg-border">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          <span>Filter</span>
        </button>
      </div>
    </div>
  );
}

const PostToolbarMemo = React.memo(PostToolbar);

export { PostToolbarMemo };
